// src/components/ui/HeroWithVideo.client.tsx

"use client"

import { memo, useState, useRef, useCallback, useEffect } from "react"
import { sendEvent } from "~/lib/gtag"
import { debounce } from "~/lib/utils/events"
import { siteConfig } from "~/lib/config/site"

// Lock-step the asset paths + aria-label with the SSR fallback +
// VideoObject JSON-LD + Sitemap entry + og:video tuple. siteConfig is
// the single source of truth; previously each surface hardcoded the
// strings and they matched only by reviewer vigilance.
const HERO = siteConfig.homepageHeroVideo

/**
 * Client-side component that handles all interactive video functionality.
 * Only renders after user interaction to maintain SEO benefits.
 */
const HeroWithVideoClientComponent = (): React.ReactNode => {
  const [showVideo, setShowVideo] = useState(false)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isDesktopAtStart, setIsDesktopAtStart] = useState<boolean | null>(null)
  // Separate refs per <video> node. The desktop element is ALWAYS mounted
  // (`hidden md:block` — display:none on mobile, but still in the React tree
  // with its ref attached), so a single shared ref would be clobbered when the
  // mobile modal mounts on top of it: `ref.current` would resolve to whichever
  // element React committed last (order/timing-dependent). Keeping them distinct
  // lets the playback effect below load()/play() the element that is actually visible.
  const desktopVideoRef = useRef<HTMLVideoElement>(null)
  const mobileVideoRef = useRef<HTMLVideoElement>(null)
  // Tracks which <video> elements already have analytics listeners, so a repeat
  // `loadeddata` (load()/seek) can't re-attach them (the listener leak below).
  const wiredVideosRef = useRef<WeakSet<HTMLVideoElement>>(new WeakSet())
  // Guards the playback effect so it initiates ONCE per open. Under Cache
  // Components, `<Activity>` re-runs effects on re-show with `showVideo` still
  // true (cache-components-migration.md §7); without this, navigating away and
  // back would replay a user-controlled video and emit a phantom play event.
  // Reset when the modal closes so a genuine reopen restarts playback.
  const playbackStartedRef = useRef(false)
  // Note: deliberately NOT auto-paused on <Activity> hide. The hero is muted
  // (no audio footgun) and its `pause` listener emits a `video_pause` analytics
  // event — a programmatic hide-pause would fire a phantom engagement event.
  // The muted loop on a hidden route is a negligible cost; correctness of the
  // analytics matters more here.

  const handleImageClick = useCallback((): void => {
    if (showVideo) return
    // Freeze the viewport class at click time — the mobile modal renders only
    // when this is `false`, and it must not flip mid-playback on a resize.
    const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
    setIsDesktopAtStart(isDesktop)
    setShowVideo(true)
    // Track video interaction
    sendEvent({
      action: "hero_video_clicked",
      label: "Hero Video Play",
    })
  }, [showVideo])

  // Begin playback once the revealed <video> is COMMITTED to the DOM. Running
  // this in an effect (rather than a click-time `setTimeout`) guarantees the
  // mobile modal's <video> — which mounts conditionally in the SAME click — has
  // its ref attached before we touch it, closing the silent null-ref window that
  // left a stuck spinner. Targets by `isDesktopAtStart`
  // (the committed value): desktop → `desktopVideoRef`, mobile → `mobileVideoRef`.
  //
  // The `playbackStartedRef` guard makes this fire ONCE per open — NOT on an
  // `<Activity>` re-show that re-runs effects with `showVideo` still true
  // (cache-components-migration.md §7), which would replay the video + emit a
  // phantom play event. Closing the modal resets the guard so a genuine mobile
  // open→close→reopen restarts playback on the freshly re-mounted <video>.
  useEffect(() => {
    if (!showVideo || isDesktopAtStart === null) {
      playbackStartedRef.current = false
      return
    }
    if (playbackStartedRef.current) return
    const el = isDesktopAtStart ? desktopVideoRef.current : mobileVideoRef.current
    if (!el) return
    playbackStartedRef.current = true
    el.load()
    void el.play().catch(() => {
      const onCanPlay = (): void => {
        void el.play().catch(() => { /* ignore */ })
        el.removeEventListener('canplay', onCanPlay)
      }
      el.addEventListener('canplay', onCanPlay, { once: true })
    })
  }, [showVideo, isDesktopAtStart])

  /**
   * Handle video ready state and attach analytics listeners.
   * - Uses debounced timeupdate to avoid high-frequency handler overhead.
   * - Emits quartile progress at 25/50/75 once per playback.
   */
  const handleVideoLoaded = useCallback((e: React.SyntheticEvent<HTMLVideoElement>): void => {
    setVideoLoaded(true)
    const el = e.currentTarget
    if (!el) return
    // If user has interacted, ensure playback begins
    if (!hasInteracted) setHasInteracted(true)
    void el.play().catch(() => { /* ignore */ })

    // Cleanup removal is INTENTIONAL. `loadeddata` is an onLoadedData event
    // handler, not an effect — React discards a handler's returned value, so
    // the previous `return cleanup` never ran, and because `loadeddata` re-fires
    // on every load()/seek the listeners re-accumulated on each fire (the leak).
    // Fix: attach the set at most once per <video> element (the WeakSet guard
    // below). No explicit removeEventListener is needed — the listeners are
    // closures over `el` and are garbage-collected together with the element
    // when React unmounts it, and the WeakSet entry is GC'd with it too.
    if (wiredVideosRef.current.has(el)) return
    wiredVideosRef.current.add(el)

    // Track video lifecycle and milestones once
    const onPlay = (): void => {
      // Emit `video_play` on every genuine play (incl. resume-from-pause, which
      // pairs with the `video_pause` below). Deliberately does NOT reset the
      // quartile counter: a pause→resume mid-clip is NOT a restart, so resetting
      // here would re-fire 25/50/75% as playback continues (inflating analytics).
      // Restarts (replay-from-start / loop wrap / seek-back) are detected by the
      // backward-time-jump check in checkProgress — the single source of truth.
      sendEvent({ action: 'video_play', label: 'Hero Video' })
    }
    const onPause = (): void => {
      const pct = el.duration ? Math.round((el.currentTime / el.duration) * 100) : 0
      sendEvent({ action: 'video_pause', label: `${pct}%`, engagement_time_msec: Math.round(el.currentTime * 1000) })
    }
    const onEnded = (): void => {
      // Emit 100% progress and completion to ensure full playback is recorded
      sendEvent({ action: 'video_progress', label: '100%', engagement_time_msec: Math.round(el.duration * 1000) })
      sendEvent({ action: 'video_complete', label: 'Hero Video', engagement_time_msec: Math.round(el.duration * 1000) })
    }
    let lastQuartile = 0
    let lastObservedTime = 0
    const checkProgress = (): void => {
      if (!el.duration || !isFinite(el.duration)) return
      // A backward jump in currentTime means the clip looped (the `loop` attr
      // wraps WITHOUT firing `play`) or the user seeked back. Restart quartile
      // tracking so 25/50/75% are recorded on every pass, not just the first.
      if (el.currentTime + 0.5 < lastObservedTime) lastQuartile = 0
      lastObservedTime = el.currentTime
      const pct = (el.currentTime / el.duration) * 100
      if (pct >= 25 && lastQuartile < 25) {
        lastQuartile = 25
        sendEvent({ action: 'video_progress', label: '25%', engagement_time_msec: Math.round(el.currentTime * 1000) })
      } else if (pct >= 50 && lastQuartile < 50) {
        lastQuartile = 50
        sendEvent({ action: 'video_progress', label: '50%', engagement_time_msec: Math.round(el.currentTime * 1000) })
      } else if (pct >= 75 && lastQuartile < 75) {
        lastQuartile = 75
        sendEvent({ action: 'video_progress', label: '75%', engagement_time_msec: Math.round(el.currentTime * 1000) })
      }
    }
    // Debounce progress checks to ~2Hz max under frequent timeupdate events
    const debouncedCheck = debounce(checkProgress, 500)
    const onTimeUpdate: EventListener = (): void => debouncedCheck()

    // `play` is NOT `{ once: true }`: it must fire on EVERY genuine play so
    // `video_play` is emitted again on a replay/resume, not only the first.
    // The listener SET is still attached at most once per <video> (guarded by
    // `wiredVideosRef` above), so this doesn't re-accumulate handlers.
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPause)
    el.addEventListener('ended', onEnded)
    el.addEventListener('timeupdate', onTimeUpdate)
  }, [hasInteracted])

  return (
    <>
      {/* Interactive overlay for triggering video */}
      <div 
        className={`absolute inset-0 cursor-pointer will-change-transform z-10 ${showVideo && videoLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ 
          transition: 'opacity 300ms ease-out',
          transform: 'translateZ(0)' // Force GPU acceleration
        }}
        onClick={handleImageClick}
      >
        {/* Theme-aware Play button overlay - entire area is clickable */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/40 dark:bg-black/60 rounded-full p-4 transition-opacity duration-200 hover:bg-black/60 dark:hover:bg-black/80">
            <div className="w-16 h-16 bg-white dark:bg-gray-100 rounded-full flex items-center justify-center shadow-lg dark:shadow-white/50 dark:ring-1 dark:ring-white/20 transition-shadow duration-200">
              {/* Play icon triangle - properly centered by accounting for triangle visual weight */}
              <svg className="w-6 h-6 text-gray-900 dark:text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Video Container - Always mounted so the post-hydration
          DOM carries a real `<video>` + `<source>` for Google's video-
          renderer pass on desktop. The primary indexing signal for the
          homepage hero already lives in the SSR raw HTML (see
          `CrawlerDiscoverableHeroVideo` in `HeroWithVideo.ssr.tsx`) +
          the VideoObject JSON-LD; this is the matching interactive
          element that becomes the user-facing player on click.
          `preload="none"` keeps the bandwidth cost at zero (the MP4
          only fetches when the user presses play); the poster attribute
          serves the same still frame the Next.js `<HeroImage />` below
          renders, so users see no visual difference until they click.
          Pre-click the element is `invisible` (visibility:hidden) rather
          than `opacity-0` — the same cloaking-signal mitigation applied
          to the SSR `<video>` (#105 `r3314732606`). Mobile users
          still get the fullscreen modal below — this desktop element
          is hidden on mobile via `md:block`. */}
      <div
        className={`absolute inset-0 z-20 hidden md:block ${
          showVideo && isDesktopAtStart === true ? '' : 'pointer-events-none'
        }`}
      >
        <video
          ref={desktopVideoRef}
          className={`w-full h-full object-cover rounded-xl will-change-transform ${
            showVideo && isDesktopAtStart === true && videoLoaded
              ? 'opacity-100'
              : 'invisible'
          }`}
          style={{
            transition: 'opacity 300ms ease-out',
            transform: 'translateZ(0)' // Force GPU acceleration
          }}
          muted
          loop
          // Controls only when actively playing — keeps the static
          // pre-click presentation clean (no native player chrome
          // overlapping the static hero image).
          controls={showVideo && isDesktopAtStart === true}
          playsInline
          preload="none" // Zero bytes until user interaction
          // Display-sized (1136px) poster shown pre-click. It matches the
          // visual of the `thumbnailUrl` declared in the homepage VideoObject
          // JSON-LD / Sitemap video extension (which keep the full-res master
          // via `posterPath`), just right-sized for this ≤1136px surface so it
          // doesn't trip `uses-responsive-images` or pre-cache the master the
          // hero `<img>` reuses. Sourced from siteConfig so all surfaces stay
          // in lock-step.
          poster={HERO.posterDisplayPath}
          aria-label={HERO.ariaLabel}
          // Hide from assistive tech until activated so the static
          // image is the announced surface pre-click. Activated via
          // the overlay's click handler below.
          aria-hidden={showVideo && isDesktopAtStart === true ? undefined : true}
          tabIndex={showVideo && isDesktopAtStart === true ? 0 : -1}
          onLoadedData={handleVideoLoaded}
          onError={(): void => {
            setShowVideo(false)
            setVideoLoaded(false)
          }}
        >
          <source src={HERO.contentPath} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>

      {/* Loading indicator */}
      {showVideo && !videoLoaded && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50 rounded-xl">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-white border-r-transparent" />
        </div>
      )}

      {/* Mobile Fullscreen Video Modal - Only visible on mobile (CSS-only) */}
      {showVideo && isDesktopAtStart === false && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center md:hidden">
          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            onClick={(): void => {
              setShowVideo(false)
              setVideoLoaded(false)
            }}
            aria-label="Close video"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Mobile Video */}
          <video
            ref={mobileVideoRef}
            className={`w-full h-full object-contain will-change-transform ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ 
              transition: 'opacity 300ms ease-out',
              transform: 'translateZ(0)' // Force GPU acceleration
            }}
            muted
            loop
            controls
            playsInline
            preload="none"
            // Same display-sized poster + aria-label + source as the desktop
            // player — pulled from siteConfig.homepageHeroVideo so all surfaces
            // stay in lock-step (og/schema keep the master via `posterPath`).
            poster={HERO.posterDisplayPath}
            aria-label={HERO.ariaLabel}
            onLoadedData={handleVideoLoaded}
            onError={(): void => {
              setShowVideo(false)
              setVideoLoaded(false)
            }}
          >
            <source src={HERO.contentPath} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Mobile Loading indicator */}
          {!videoLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-white border-r-transparent" />
            </div>
          )}
        </div>
      )}
    </>
  )
}

const HeroWithVideoClient = memo(HeroWithVideoClientComponent)

export default HeroWithVideoClient
